import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CSSProperties } from "react";
import type { RoundName } from "../../domain/round/types";
import { vehicleVisualIdentity } from "../../domain/transport/identity";
import type { TransportDirection, TransportPlan, VehicleKeyPolicy, VehicleTrip } from "../../domain/transport/types";
import { ApiError, api, type RoundStaffSummary, type TournamentSummary, type WorkspaceResource } from "../../lib/api";
import { initialRoundName, initialSeason, initialTournamentId, persistRoundSelection } from "../../lib/roundSelection";
import "../rounds/round-workspace.css";
import "./transport-page.css";
import "./transport-editor.css";

const ROUNDS: RoundName[] = ["R1", "R2", "R3", "R4"];

function clonePlan(plan: TransportPlan): TransportPlan {
  return structuredClone(plan);
}

function chooseTournament(rows: TournamentSummary[]): string {
  if (!rows.length) return "";
  const today = new Date().toISOString().slice(0, 10);
  const active = rows.find((row) => row.startDate <= today && (!row.endDate || row.endDate >= today));
  if (active) return active.id;
  const upcoming = rows.filter((row) => row.startDate && row.startDate > today).sort((a, b) => a.startDate.localeCompare(b.startDate));
  if (upcoming.length) return upcoming[0].id;
  const started = rows.filter((row) => row.startDate && row.startDate <= today);
  return (started.at(-1) || rows[0]).id;
}

function clock(minutes: number | null | undefined) {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return "—";
  const value = Math.max(0, Math.round(minutes));
  return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
}

function parseClock(value: string, fallback: number) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return fallback;
  return Math.max(0, Number(match[1]) * 60 + Math.min(59, Number(match[2])));
}

function issueLabel(severity: string) {
  if (severity === "blocker") return "BLOCKER";
  if (severity === "warning") return "UWAGA";
  return "INFO";
}

type EditorMaps = {
  people: Map<string, string>;
  vehicles: Map<string, WorkspaceResource>;
  bases: Map<string, string>;
};

function TripReadCard({ trip, maps }: { trip: VehicleTrip; maps: EditorMaps }) {
  const vehicle = maps.vehicles.get(trip.vehicleId);
  const visual = vehicleVisualIdentity(trip.vehicleId);
  const accentStyle: CSSProperties = { backgroundColor: visual.background, color: visual.foreground, borderColor: visual.border };
  return (
    <article className="transport-trip-card">
      <header className="transport-trip-head">
        <div><span className="transport-vehicle-badge" style={accentStyle}>{vehicle?.name || trip.vehicleId}</span>{trip.shuttle ? <span className="transport-shuttle-badge">SHUTTLE {trip.tripNumber}</span> : null}{trip.manualFleetVehicle ? <span className="transport-manual-badge">FLOTA +</span> : null}</div>
        <div className="transport-trip-time"><strong>{clock(trip.departAtMinutes)}</strong><span>→ {clock(trip.arriveAtMinutes)}</span></div>
      </header>
      <div className="transport-trip-meta">
        <span><small>Nocleg</small><strong>{maps.bases.get(trip.baseId) || vehicle?.baseName || trip.baseId}</strong></span>
        <span><small>{trip.vehicleId.startsWith("uber-") ? "Zamawia" : "Kierowca"}</small><strong>{maps.people.get(trip.driverPersonId) || (vehicle?.external ? "Autobus organizatora" : "—")}</strong></span>
        <span><small>Obsada</small><strong>{trip.passengerPersonIds.length + (trip.driverPersonId ? 1 : 0)} / {vehicle?.capacity || "?"}</strong></span>
      </div>
      <div className="transport-passengers"><small>Osoby w aucie</small><div>{trip.passengerPersonIds.length ? trip.passengerPersonIds.map((id) => <span key={id}>{maps.people.get(id) || id}</span>) : <span className="muted">Brak pasażerów</span>}</div></div>
    </article>
  );
}

function TripEditor({
  trip,
  direction,
  staff,
  vehicles,
  maps,
  outboundByVehicle,
  onChange,
  onDelete,
}: {
  trip: VehicleTrip;
  direction: TransportDirection;
  staff: RoundStaffSummary[];
  vehicles: WorkspaceResource[];
  maps: EditorMaps;
  outboundByVehicle: Map<string, VehicleTrip>;
  onChange: (patch: Partial<VehicleTrip>) => void;
  onDelete: () => void;
}) {
  const [shiftAmount, setShiftAmount] = useState("20");
  const vehicle = maps.vehicles.get(trip.vehicleId);
  const baseId = trip.baseId || vehicle?.baseId || "";
  const normalVehicles = vehicles.filter((item) => !item.setup && item.active !== false);
  const drivers = staff.filter((person) => person.driverEligible && (!baseId || person.baseId === baseId));
  const people = staff.filter((person) => person.role === "scorer" && person.transportIncluded && (!baseId || person.baseId === baseId));
  const occupied = trip.passengerPersonIds.length + (trip.driverPersonId ? 1 : 0);
  const capacity = vehicle?.capacity || 0;

  const selectVehicle = (vehicleId: string) => {
    const nextVehicle = maps.vehicles.get(vehicleId);
    const outbound = outboundByVehicle.get(vehicleId);
    const nextBaseId = direction === "return" && outbound ? outbound.baseId : nextVehicle?.baseId || trip.baseId;
    onChange({
      vehicleId,
      baseId: nextBaseId,
      manualFleetVehicle: direction === "return" ? !outboundByVehicle.has(vehicleId) : false,
      ...(nextVehicle?.external ? { driverPersonId: "" } : {}),
    });
  };

  const togglePassenger = (personId: string) => {
    if (personId === trip.driverPersonId) return;
    const exists = trip.passengerPersonIds.includes(personId);
    onChange({ passengerPersonIds: exists ? trip.passengerPersonIds.filter((id) => id !== personId) : [...trip.passengerPersonIds, personId] });
  };

  return (
    <article className={`transport-edit-card ${capacity > 0 && occupied > capacity ? "invalid" : ""}`}>
      <div className="transport-edit-head">
        <div><span className="eyebrow">{direction === "to-course" ? "DOJAZD" : "POWRÓT"}</span><strong>{vehicle?.name || "Wybierz auto"}</strong></div>
        <div className="transport-trip-shift">
          <input type="number" min={1} max={180} value={shiftAmount} onChange={(event) => setShiftAmount(event.target.value)} />
          <button type="button" title="Przesuń wcześniej" onClick={() => { const delta = Number(shiftAmount) || 0; onChange({ departAtMinutes: trip.departAtMinutes - delta, arriveAtMinutes: trip.arriveAtMinutes - delta }); }}>← min</button>
          <button type="button" title="Przesuń później" onClick={() => { const delta = Number(shiftAmount) || 0; onChange({ departAtMinutes: trip.departAtMinutes + delta, arriveAtMinutes: trip.arriveAtMinutes + delta }); }}>min →</button>
        </div>
        <button type="button" onClick={onDelete} title="Usuń kurs">×</button>
      </div>
      <div className="transport-edit-grid">
        <label><span>Auto</span><select value={trip.vehicleId} onChange={(event) => selectVehicle(event.target.value)}><option value="">Wybierz…</option>{normalVehicles.map((item) => <option key={item.id} value={item.id}>{item.name}{item.external ? " · AUTOBUS" : ""}{item.capacity ? ` · ${item.capacity} os.` : ""}</option>)}</select></label>
        {vehicle?.external ? (
          <label><span>Kierowca</span><input value="Autobus organizatora — bez naszego kierowcy" disabled /></label>
        ) : (
          <label><span>{trip.vehicleId.startsWith("uber-") ? "Zamawia" : "Kierowca"}</span><select value={trip.driverPersonId} onChange={(event) => onChange({ driverPersonId: event.target.value, passengerPersonIds: trip.passengerPersonIds.filter((id) => id !== event.target.value) })}><option value="">Wybierz…</option>{drivers.map((person) => <option key={person.personId} value={person.personId}>{person.displayName}</option>)}</select></label>
        )}
        <label><span>Wyjazd</span><input type="time" value={clock(trip.departAtMinutes)} onChange={(event) => onChange({ departAtMinutes: parseClock(event.target.value, trip.departAtMinutes) })} /></label>
        <label><span>Przyjazd</span><input type="time" value={clock(trip.arriveAtMinutes)} onChange={(event) => onChange({ arriveAtMinutes: parseClock(event.target.value, trip.arriveAtMinutes) })} /></label>
      </div>
      <div className="transport-edit-summary">
        <span>Nocleg <strong>{maps.bases.get(trip.baseId) || vehicle?.baseName || "—"}</strong></span>
        <span className={capacity > 0 && occupied > capacity ? "bad" : ""}>Obsada <strong>{occupied}/{capacity || "?"}</strong></span>
        {direction === "return" ? <label className="transport-shuttle-toggle"><input type="checkbox" checked={trip.shuttle} onChange={(event) => onChange({ shuttle: event.target.checked })} /> shuttle / drugi kurs</label> : null}
      </div>
      <div className="transport-people-picker">
        <span>Osoby w aucie</span>
        <div>{people.map((person) => {
          const checked = trip.passengerPersonIds.includes(person.personId) || trip.driverPersonId === person.personId;
          const isDriver = trip.driverPersonId === person.personId;
          return <label key={person.personId} className={isDriver ? "driver" : ""}><input type="checkbox" checked={checked} disabled={isDriver} onChange={() => togglePassenger(person.personId)} /><strong>{person.displayName}</strong>{isDriver ? <small>{trip.vehicleId.startsWith("uber-") ? "ZAMAWIA" : "KIEROWCA"}</small> : null}</label>;
        })}</div>
      </div>
    </article>
  );
}

export function TransportEditorPage() {
  const queryClient = useQueryClient();
  const [season, setSeason] = useState(initialSeason);
  const [tournamentId, setTournamentId] = useState(initialTournamentId);
  const [round, setRound] = useState<RoundName>(initialRoundName);
  const [draft, setDraft] = useState<TransportPlan | null>(null);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [notice, setNotice] = useState("");
  const [addReturnVehicleId, setAddReturnVehicleId] = useState("");
  const [bulkDepartShift, setBulkDepartShift] = useState("20");
  const [bulkReturnShift, setBulkReturnShift] = useState("20");

  const tournaments = useQuery({ queryKey: ["tournaments", season], queryFn: () => api.tournaments(season) });
  useEffect(() => {
    const rows = tournaments.data?.tournaments || [];
    if (rows.length && (!tournamentId || !rows.some((row) => row.id === tournamentId))) setTournamentId(chooseTournament(rows));
  }, [tournaments.data, tournamentId]);
  useEffect(() => {
    if (tournamentId) persistRoundSelection(tournamentId, round, season);
  }, [tournamentId, round, season]);

  const workspace = useQuery({ queryKey: ["round-workspace", tournamentId, round], queryFn: () => api.roundWorkspace(tournamentId, round), enabled: Boolean(tournamentId) });
  useEffect(() => {
    setDraft(workspace.data?.transport ? clonePlan(workspace.data.transport) : null);
    setEditing(false);
    setDirty(false);
    setNotice("");
    setAddReturnVehicleId("");
  }, [workspace.data]);

  const data = workspace.data;
  const selectedTournament = tournaments.data?.tournaments.find((item) => item.id === tournamentId);
  const readOnly = Boolean(data?.selectedSource === "confirmed" && !data.availability.working);
  const vehicles = data?.resources.vehicles || [];
  const staff = data?.staff || [];
  const maps = useMemo<EditorMaps>(() => ({
    people: new Map(staff.map((person) => [person.personId, person.displayName])),
    vehicles: new Map(vehicles.map((vehicle) => [vehicle.id, vehicle])),
    bases: new Map((data?.resources.bases || []).map((base) => [base.id, base.name])),
  }), [staff, vehicles, data?.resources.bases]);
  const outboundByVehicle = useMemo(() => new Map((draft?.departures || []).map((trip) => [trip.vehicleId, trip])), [draft?.departures]);
  const usedReturnIds = new Set((draft?.returns || []).map((trip) => trip.vehicleId));
  const unusedReturnVehicles = vehicles.filter((vehicle) => !vehicle.setup && vehicle.active !== false && !usedReturnIds.has(vehicle.id));

  const mutateTrip = (direction: TransportDirection, tripId: string, patch: Partial<VehicleTrip>) => {
    if (!draft) return;
    const key = direction === "to-course" ? "departures" : "returns";
    setDraft({ ...draft, [key]: draft[key].map((trip) => trip.id === tripId ? { ...trip, ...patch } : trip), issues: draft.issues.filter((issue) => !issue.id.startsWith("manual:")) });
    setDirty(true);
    setNotice("");
  };

  const shiftAllTrips = (direction: TransportDirection, deltaMinutes: number) => {
    if (!draft || !deltaMinutes) return;
    const key = direction === "to-course" ? "departures" : "returns";
    setDraft({
      ...draft,
      [key]: draft[key].map((trip) => ({ ...trip, departAtMinutes: trip.departAtMinutes + deltaMinutes, arriveAtMinutes: trip.arriveAtMinutes + deltaMinutes })),
      issues: draft.issues.filter((issue) => !issue.id.startsWith("manual:")),
    });
    setDirty(true);
    setNotice("");
  };

  const deleteTrip = (direction: TransportDirection, tripId: string) => {
    if (!draft) return;
    const key = direction === "to-course" ? "departures" : "returns";
    setDraft({ ...draft, [key]: draft[key].filter((trip) => trip.id !== tripId), issues: draft.issues.filter((issue) => !issue.id.startsWith("manual:")) });
    setDirty(true);
  };

  const addReturnVehicle = () => {
    if (!draft || !addReturnVehicleId) return;
    const vehicle = maps.vehicles.get(addReturnVehicleId);
    if (!vehicle) return;
    const outbound = outboundByVehicle.get(vehicle.id);
    const baseId = outbound?.baseId || vehicle.baseId || "";
    const trip: VehicleTrip = {
      id: `manual:return:${vehicle.id}:${crypto.randomUUID()}`,
      direction: "return",
      baseId,
      vehicleId: vehicle.id,
      driverPersonId: outbound?.driverPersonId || "",
      passengerPersonIds: [],
      departAtMinutes: outbound?.arriveAtMinutes || 0,
      arriveAtMinutes: outbound?.arriveAtMinutes || 0,
      tripNumber: 1,
      shuttle: false,
      waitSpreadMinutes: 0,
      manualFleetVehicle: !outbound,
    };
    setDraft({ ...draft, returns: [...draft.returns, trip] });
    setDirty(true);
    setAddReturnVehicleId("");
  };

  const validate = useMutation({
    mutationFn: () => {
      if (!draft || !data) throw new Error("Brak transportu do sprawdzenia.");
      return api.validateTransport(tournamentId, round, data.workspaceRevision, draft);
    },
    onSuccess: (result) => {
      setDraft(result.transport);
      setNotice(result.valid ? "Transport przeszedł walidację." : `Transport ma ${result.blockers.length} błędów blokujących.`);
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!draft || !data?.schedule || !data) throw new Error("Brak danych do zapisania.");
      const checked = await api.validateTransport(tournamentId, round, data.workspaceRevision, draft);
      setDraft(checked.transport);
      if (!checked.valid) throw new Error(`Transport ma ${checked.blockers.length} błędów blokujących. Popraw je przed zapisem.`);
      return api.saveRoundWorkspace(tournamentId, round, { expectedRevision: data.workspaceRevision, schedule: data.schedule, transport: checked.transport, transportStale: false });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["round-workspace", tournamentId, round], result);
      setDraft(result.transport ? clonePlan(result.transport) : null);
      setEditing(false);
      setDirty(false);
      setNotice(`Zapisano ręczny transport w workspace r${result.workspaceRevision}.`);
    },
  });

  const error = workspace.error || tournaments.error || save.error || validate.error;
  const errorMessage = error instanceof ApiError ? error.message : error instanceof Error ? error.message : "";
  const blockerCount = draft?.issues.filter((issue) => issue.severity === "blocker").length || 0;
  const warningCount = draft?.issues.filter((issue) => issue.severity === "warning").length || 0;

  return (
    <section className="page-grid transport-page transport-editor-page">
      <article className="panel round-context-panel">
        <div className="round-context-copy"><span className="eyebrow">TRANSPORT WORKSPACE</span><h2>{selectedTournament?.name || "Wybierz turniej"}</h2><p>Dojazdy i powroty są osobnymi kierunkami, ale korzystają z jednej fizycznej floty i jednego revision-safe workspace.</p></div>
        <div className="round-context-controls">
          <label><span>Sezon</span><select value={season} onChange={(event) => setSeason(Number(event.target.value))}>{[2026, 2025, 2024].map((value) => <option key={value}>{value}</option>)}</select></label>
          <label className="tournament-select"><span>Turniej</span><select value={tournamentId} onChange={(event) => setTournamentId(event.target.value)}>{(tournaments.data?.tournaments || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <div className="round-switch">{ROUNDS.map((value) => <button key={value} type="button" className={round === value ? "active" : ""} onClick={() => setRound(value)}>{value}</button>)}</div>
        </div>
      </article>

      {readOnly ? <div className="transport-editor-notice">Runda zatwierdzona — transport tylko do odczytu.</div> : null}
      {notice || errorMessage ? <div className={`transport-editor-notice ${errorMessage ? "error" : ""}`}>{errorMessage || notice}</div> : null}

      {data && draft ? (
        <>
          <article className="panel transport-editor-toolbar">
            <div><span className={dirty ? "transport-edit-state dirty" : "transport-edit-state"}>{dirty ? "Niezapisane zmiany" : "Zapisany plan"}</span><span>workspace r{data.workspaceRevision}</span></div>
            <div className="transport-policy-edit"><label><span>Kluczyki</span><select disabled={!editing || readOnly} value={draft.policy.keyPolicy} onChange={(event) => { setDraft({ ...draft, policy: { ...draft.policy, keyPolicy: event.target.value as VehicleKeyPolicy } }); setDirty(true); }}><option value="free">Elastyczne</option><option value="same-car">Ten sam samochód</option><option value="same-driver">Ten sam kierowca</option></select></label></div>
            <div className="transport-editor-actions">
              {!editing ? <button className="secondary-button" type="button" disabled={readOnly} onClick={() => setEditing(true)}>Edytuj transport</button> : <button className="secondary-button" type="button" onClick={() => { setDraft(data.transport ? clonePlan(data.transport) : null); setEditing(false); setDirty(false); setNotice(""); }}>Anuluj</button>}
              <button className="secondary-button" type="button" disabled={!editing || validate.isPending} onClick={() => validate.mutate()}>{validate.isPending ? "Sprawdzam…" : "Sprawdź transport"}</button>
              <button className="primary-button" type="button" disabled={!editing || !dirty || save.isPending || readOnly} onClick={() => save.mutate()}>{save.isPending ? "Zapisuję…" : "Zapisz ręczny transport"}</button>
            </div>
          </article>

          <div className="round-kpi-grid transport-kpi-grid">
            <article className="panel round-kpi"><span>Dojazdy</span><strong>{draft.departures.length}</strong><small>{new Set(draft.departures.map((trip) => trip.vehicleId)).size} aut</small></article>
            <article className="panel round-kpi"><span>Powroty</span><strong>{draft.returns.length}</strong><small>{new Set(draft.returns.map((trip) => trip.vehicleId)).size} aut</small></article>
            <article className="panel round-kpi"><span>Max czekanie</span><strong>{Math.max(draft.metrics.morningMaxWaitMinutes, draft.metrics.returnMaxWaitMinutes)} min</strong><small>metryki z ostatniego przeliczenia</small></article>
            <article className={`panel round-kpi ${blockerCount ? "transport-kpi-danger" : ""}`}><span>Walidacja</span><strong>{blockerCount ? `${blockerCount} blocker` : "OK"}</strong><small>{warningCount} ostrzeżeń</small></article>
          </div>

          {draft.issues.length ? <article className="panel transport-issues-panel"><header className="round-section-head"><div><span className="eyebrow">VALIDATION</span><h3>Uwagi transportowe</h3></div></header><div className="transport-issue-list">{draft.issues.map((issue) => <div key={issue.id} className={`transport-issue ${issue.severity}`}><span>{issueLabel(issue.severity)}</span><div><strong>{issue.code}</strong><p>{issue.message}</p></div></div>)}</div></article> : null}

          <div className="transport-directions transport-editor-directions">
            <article className="panel transport-board">
              <header className="round-section-head"><div><span className="eyebrow">TO COURSE</span><h3>Ręczna edycja dojazdów na pole</h3></div><span className="transport-board-count">{draft.departures.length} kursów</span></header>
              {editing ? <div className="transport-bulk-shift"><span>Wszystkie kursy</span><input type="number" min={1} max={180} value={bulkDepartShift} onChange={(event) => setBulkDepartShift(event.target.value)} /><button type="button" className="secondary-button" onClick={() => shiftAllTrips("to-course", -(Number(bulkDepartShift) || 0))}>Wcześniej</button><button type="button" className="secondary-button" onClick={() => shiftAllTrips("to-course", Number(bulkDepartShift) || 0)}>Później</button></div> : null}
              <div className="transport-trip-grid">{draft.departures.map((trip) => editing ? <TripEditor key={trip.id} trip={trip} direction="to-course" staff={staff} vehicles={vehicles} maps={maps} outboundByVehicle={outboundByVehicle} onChange={(patch) => mutateTrip("to-course", trip.id, patch)} onDelete={() => deleteTrip("to-course", trip.id)} /> : <TripReadCard key={trip.id} trip={trip} maps={maps} />)}</div>
            </article>

            <article className="panel transport-board">
              <header className="round-section-head"><div><span className="eyebrow">RETURN</span><h3>Ręczna edycja powrotów</h3></div><span className="transport-board-count">{draft.returns.length} kursów</span></header>
              {editing ? <div className="transport-bulk-shift"><span>Wszystkie kursy</span><input type="number" min={1} max={180} value={bulkReturnShift} onChange={(event) => setBulkReturnShift(event.target.value)} /><button type="button" className="secondary-button" onClick={() => shiftAllTrips("return", -(Number(bulkReturnShift) || 0))}>Wcześniej</button><button type="button" className="secondary-button" onClick={() => shiftAllTrips("return", Number(bulkReturnShift) || 0)}>Później</button></div> : null}
              {editing ? <div className="transport-add-fleet"><select value={addReturnVehicleId} onChange={(event) => setAddReturnVehicleId(event.target.value)}><option value="">Wybierz niewykorzystane auto…</option>{unusedReturnVehicles.map((vehicle) => <option key={vehicle.id} value={vehicle.id}>{vehicle.name}{vehicle.baseName ? ` · ${vehicle.baseName}` : ""}</option>)}</select><button className="secondary-button" type="button" disabled={!addReturnVehicleId} onClick={addReturnVehicle}>+ Dodaj auto z floty</button></div> : null}
              <div className="transport-trip-grid">{draft.returns.map((trip) => editing ? <TripEditor key={trip.id} trip={trip} direction="return" staff={staff} vehicles={vehicles} maps={maps} outboundByVehicle={outboundByVehicle} onChange={(patch) => mutateTrip("return", trip.id, patch)} onDelete={() => deleteTrip("return", trip.id)} /> : <TripReadCard key={trip.id} trip={trip} maps={maps} />)}</div>
            </article>
          </div>
        </>
      ) : workspace.isPending ? <article className="panel round-loading"><div className="round-loading-bar" /><span>Wczytuję transport…</span></article> : <article className="panel round-empty">Ta runda nie ma jeszcze transportu. Najpierw wygeneruj lub przelicz plan.</article>}
    </section>
  );
}
